import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Loader2, Moon, Sun } from "lucide-react";
import burjKhalifaBackground from "@/assets/burj-khalifa-background.jpg";
import loginBackgroundDark from "@/assets/login-background-dark.jpg";
import mainLogo from "@/assets/main-logo.png";
import { getBuildLabel } from "@/config/env";
import { useTheme } from "next-themes";
import { cn } from "@/lib/utils";

const Auth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [loginValue, setLoginValue] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { signIn } = useAuth();
  const { toast } = useToast();
  const { resolvedTheme, setTheme } = useTheme();
  const isDarkMode = mounted && resolvedTheme === "dark";

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const { error } = await signIn(loginValue, loginPassword);
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Signed in successfully",
      });
    }
    setIsLoading(false);
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden">
      <div className="absolute right-6 top-6 z-20">
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => setTheme(isDarkMode ? "light" : "dark")}
          className="border-white/40 bg-black/30 text-white backdrop-blur-sm hover:bg-black/45"
          aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          title={isDarkMode ? "Light mode" : "Dark mode"}
        >
          {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>
      </div>

      <div className="absolute inset-0">
        <div
          className={cn(
            "absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-700 ease-in-out",
            isDarkMode ? "opacity-0" : "opacity-100",
          )}
          style={{ backgroundImage: `url(${burjKhalifaBackground})` }}
        />
        <div
          className={cn(
            "absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-700 ease-in-out",
            isDarkMode ? "opacity-100" : "opacity-0",
          )}
          style={{ backgroundImage: `url(${loginBackgroundDark})` }}
        />
        <div
          className={cn(
            "absolute inset-0 transition-colors duration-700 ease-in-out",
            isDarkMode
              ? "bg-gradient-to-b from-black/88 via-black/68 to-slate-950/88"
              : "bg-gradient-to-b from-black/60 via-black/40 to-black/60",
          )}
        />
      </div>

      <div className="relative z-10 w-full max-w-md px-6">
        <div
          className={cn(
            "rounded-lg border shadow-2xl transition-colors duration-500",
            isDarkMode
              ? "border-white/70 bg-slate-200/30 backdrop-blur-md"
              : "border-white/90 bg-slate-100/90 backdrop-blur-sm",
          )}
        >
          <div className="flex flex-col items-center px-8 py-10">
            <img src={mainLogo} alt="High Class logo" className="mb-6 h-28 w-auto max-w-[280px] object-contain" />
          </div>

          <form onSubmit={handleLogin} className="space-y-4 px-8 pb-8">
            <div>
              <Input
                type="text"
                placeholder="Email or username"
                value={loginValue}
                onChange={(e) => setLoginValue(e.target.value)}
                autoComplete="username"
                required
                className={cn(
                  "h-12 rounded-md",
                  isDarkMode
                    ? "border-slate-500 bg-slate-300 text-slate-900 placeholder:text-slate-700 autofill:border-slate-500 autofill:[-webkit-text-fill-color:rgb(15_23_42)] autofill:shadow-[inset_0_0_0px_1000px_rgb(203_213_225)]"
                    : "border-gray-300 bg-gray-50 text-gray-900 placeholder:text-gray-400 autofill:border-gray-300 autofill:[-webkit-text-fill-color:rgb(17_24_39)] autofill:shadow-[inset_0_0_0px_1000px_rgb(249_250_251)]",
                )}
              />
            </div>
            <div className="space-y-2">
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className={cn(
                    "h-12 rounded-md pr-11",
                    isDarkMode
                      ? "border-slate-500 bg-slate-300 text-slate-900 placeholder:text-slate-700 autofill:border-slate-500 autofill:[-webkit-text-fill-color:rgb(15_23_42)] autofill:shadow-[inset_0_0_0px_1000px_rgb(203_213_225)]"
                      : "border-gray-300 bg-gray-50 text-gray-900 placeholder:text-gray-400 autofill:border-gray-300 autofill:[-webkit-text-fill-color:rgb(17_24_39)] autofill:shadow-[inset_0_0_0px_1000px_rgb(249_250_251)]",
                  )}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className={cn(
                    "absolute right-3 top-1/2 -translate-y-1/2 transition-colors",
                    isDarkMode ? "text-slate-700 hover:text-slate-900" : "text-gray-500 hover:text-gray-700",
                  )}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={isLoading}
              className="h-12 w-full rounded-md bg-[#C9A961] text-white hover:bg-[#B89951] font-medium"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>
        </div>
      </div>
      <footer className="absolute bottom-2 left-0 right-0 text-center">
        <span className="text-[10px] text-white/70">{getBuildLabel()}</span>
      </footer>
    </div>
  );
};

export default Auth;
